import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import { PayoutRequestQueue } from "@/components/payout-request-queue";

import { EmptyState } from "@/components/empty-state";
import { PayoutLedgerPanel } from "@/components/payout-ledger-panel";
import { SectionHeading } from "@/components/section-heading";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/lib/i18n";
import { formatMoney } from "@/lib/money";
import {
  acknowledgeOpsAlert,
  listOpsAlerts,
  getFinancePermissions,
  resolveTipManually,
} from "@/lib/payments/admin-tips.functions";
import { getFinancialOverview } from "@/lib/tips.functions";

export const Route = createFileRoute("/_authenticated/admin/finance")({
  head: () => ({
    meta: [
      { title: "Financial overview — Quibblies" },
      {
        name: "description",
        content: "Restricted financial overview of Quibblies tipping activity and platform fees.",
      },
      { property: "og:title", content: "Financial overview — Quibblies" },
      { property: "og:description", content: "Restricted internal financial reporting." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: FinancePage,
});

function FinancePage() {
  const { language } = useI18n();
  const locale = language === "en" ? "en-GB" : language;
  const load = useServerFn(getFinancialOverview);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-finance"],
    queryFn: () => load(),
    retry: false,
  });

  const queryClient = useQueryClient();
  const loadAlerts = useServerFn(listOpsAlerts);
  const alerts = useQuery({
    queryKey: ["ops-alerts"],
    queryFn: () => loadAlerts(),
    retry: false,
  });

  const ackAlert = useServerFn(acknowledgeOpsAlert);
  const acknowledge = useMutation({
    mutationFn: (alertId: string) => ackAlert({ data: { alertId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ops-alerts"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const loadPermissions = useServerFn(getFinancePermissions);
  const permissions = useQuery({
    queryKey: ["finance-permissions"],
    queryFn: () => loadPermissions(),
    retry: false,
  });
  // Manual money movement needs an explicit grant from the Owner on top of the
  // administrator role, so the buttons stay hidden for anyone else.
  const canResolveTips = permissions.data?.canResolveTips === true;

  const resolveTip = useServerFn(resolveTipManually);
  const [busyTip, setBusyTip] = React.useState<string | null>(null);
  const resolve = useMutation({
    mutationFn: (input: { tipId: string; action: "refund" | "dispute" | "record_refund" }) =>
      resolveTip({ data: input }),
    onMutate: (input) => setBusyTip(input.tipId),
    onSettled: () => setBusyTip(null),
    onSuccess: (result) => {
      toast.success(result.message);
      void queryClient.invalidateQueries({ queryKey: ["admin-finance"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  /** Manual actions are irreversible, so each one is confirmed explicitly. */
  function act(tipId: string, action: "refund" | "dispute" | "record_refund") {
    if (!canResolveTips) {
      toast.error(
        permissions.data?.reason ??
          "You need financial access from the Owner to move money manually.",
      );
      return;
    }
    const prompt =
      action === "refund"
        ? "Refund this tip through the payment provider? The supporter is repaid and the writer's earnings are reduced."
        : action === "dispute"
          ? "Record this tip as disputed? It will be held and both parties notified."
          : "Record this tip as refunded outside the platform? No money is moved by Quibblies.";
    if (!window.confirm(prompt)) return;
    resolve.mutate({ tipId, action });
  }

  const openAlerts = (alerts.data ?? []).filter((alert) => !alert.acknowledgedAt);

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Financial overview"
        description="Restricted to the Owner and Administrators granted financial access. Moderators never see financial data."
      />
      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        <Link to="/admin/ops-settings" className="text-primary underline-offset-4 hover:underline">
          Alert thresholds &amp; quiet hours
        </Link>
        <Link to="/admin/tip-actions" className="text-primary underline-offset-4 hover:underline">
          Manual refund &amp; dispute log
        </Link>
        <Link to="/admin/tip-replay" className="text-primary underline-offset-4 hover:underline">
          Stuck tips &amp; replay
        </Link>
      </div>


      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : error ? (
        <div className="mt-8">
          <EmptyState
            icon={Lock}
            title="Financial access required"
            description="Your account does not have financial access. The Owner can grant it."
          />
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          {openAlerts.length > 0 ? (
            <div className="space-y-3">
              {openAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-destructive/50 bg-destructive/10 p-4"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle
                      className="mt-0.5 h-5 w-5 text-destructive"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="font-medium text-foreground">
                        {alert.severity === "critical" ? "Critical" : "Warning"} —{" "}
                        {alert.metric.replace(/_/g, " ")}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">{alert.detail}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Raised {new Date(alert.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="soft"
                    size="sm"
                    disabled={acknowledge.isPending}
                    onClick={() => acknowledge.mutate(alert.id)}
                  >
                    Acknowledge
                  </Button>
                </div>
              ))}
            </div>
          ) : null}

          <PayoutRequestQueue />

          <PayoutLedgerPanel locale={locale} />


          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data?.totals ?? []).map((total) => (
              <div
                key={total.currency}
                className="rounded-2xl border border-border/60 bg-surface/50 p-5"
              >
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {total.currency} completed
                </p>
                <p className="mt-1 font-display text-2xl text-foreground">
                  {formatMoney(total.completed, total.currency, locale)}
                </p>
                <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <dt>Platform fees</dt>
                    <dd>{formatMoney(total.fees, total.currency, locale)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Refunded</dt>
                    <dd>{formatMoney(total.refunded, total.currency, locale)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Failed</dt>
                    <dd>{formatMoney(total.failed, total.currency, locale)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Disputed</dt>
                    <dd>{formatMoney(total.disputed, total.currency, locale)}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Writer</th>
                  <th className="px-4 py-3">Supporter</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Fee</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.transactions ?? []).map((row) => (
                  <tr key={row.id} className="border-t border-border/50">
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(row.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-foreground">{row.writer ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {row.sender ?? "Guest"}
                      {row.anonymousToWriter ? " (anonymous to writer)" : ""}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {formatMoney(row.amountMinor, row.currency, locale)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatMoney(row.feeMinor, row.currency, locale)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{row.status}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {row.provider ?? "—"} {row.reference ?? ""}
                    </td>
                    <td className="px-4 py-3">
                      {row.status === "completed" ? (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="soft"
                            disabled={busyTip === row.id || !canResolveTips}
                            onClick={() => act(row.id, "refund")}
                          >
                            Refund
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyTip === row.id || !canResolveTips}
                            onClick={() => act(row.id, "record_refund")}
                          >
                            Record refund
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyTip === row.id || !canResolveTips}
                            onClick={() => act(row.id, "dispute")}
                          >
                            Record dispute
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
